import {LogLevel, NfcCard} from "@variocube/app-api";
import {
    classNames,
    layout,
    PushButton,
    theme,
    useCardListener,
    usePlatform,
    usePreventDeactivate,
    View,
    ViewActions,
    ViewContent,
    ViewHeader,
    ViewHeaderItem
} from "@variocube/cube-ui";
import * as React from "react";
import {useCallback, useMemo, useState} from "react";
import {getOccupancyDeliveries, LearnedPickupKey, PickupEvent, Recipient} from "./types";
import {stylesheet} from "typestyle";
import {useLocalization} from "../i18n";


interface LearnPickupKeyViewProps {
    pickupEvent: PickupEvent;
    partialPickup: boolean;
    onConfirm: (pickupEvent: PickupEvent, partialPickup: boolean, learnedPickupKey?: LearnedPickupKey) => void;
}

export function LearnPickupKeyView({pickupEvent, partialPickup, onConfirm}: LearnPickupKeyViewProps) {
    usePreventDeactivate();

    const {t} = useLocalization();
    const platform = usePlatform();

    const recipients = useMemo<Recipient[]>(() => {
        if(pickupEvent.occupancies?.length > 0) {
            const deliveries = getOccupancyDeliveries(pickupEvent.occupancies[0]) ?? [];
            return deliveries?.flatMap(delivery => delivery.recipients);
        }
        return [];
    }, [pickupEvent]);

    const [selectedRecipient, setSelectedRecipient] = useState<number | undefined>(undefined);
    const [card, setCard] = useState<NfcCard | undefined>(undefined);

    const recipientName = useMemo<string | undefined>(() => {
        if(recipients && selectedRecipient != undefined) {
            return recipients[selectedRecipient].recipientName;
        }
        return undefined;
    }, [recipients, selectedRecipient]);

    useCardListener(async (card?: NfcCard) => {
        if(card && selectedRecipient != undefined) {
            await platform.log(LogLevel.Trace, "NFC card presented for recipient", {card, selectedRecipient});
        }
        setCard(card);
    });

    const handleSelectRecipient = useCallback((index: number) => {
        setSelectedRecipient(index);
        setCard(undefined);
    }, []);

    const learnedPickupKey = useMemo<LearnedPickupKey | undefined>(() => {
        if(selectedRecipient != undefined && card) {
            return {
                recipientIndex: selectedRecipient,
                pickupKey: atob(card.uid).trim()
            };
        }
        return undefined;
    }, [selectedRecipient, card]);

    const handleConfirm = useCallback(() => onConfirm(pickupEvent, partialPickup, learnedPickupKey), [onConfirm, pickupEvent, partialPickup, learnedPickupKey]);

    const handleCancel = useCallback(() => {
        setSelectedRecipient(undefined);
        setCard(undefined);
    }, []);

    return (
        <View>
            <ViewHeader className={theme.lessContrast}>
                <ViewHeaderItem className={classNames(layout.paddingMedium, styles.branding)}>
                    {t("LearnPickupKeyTitle")}
                </ViewHeaderItem>
                <ViewActions>
                    <PushButton className={styles.button} onClick={handleConfirm}>{t("Continue")}</PushButton>
                </ViewActions>
            </ViewHeader>
            <ViewContent className={classNames(layout.paddingMedium)}>
                {selectedRecipient == undefined &&
                <>
                    <div>{t("SelectYourName")}</div>
                    <div>
                        {recipients.map((r, i) =>
                            <div key={r.recipientName} className={styles.recipientListElement}>
                                <PushButton onClick={() => handleSelectRecipient(i)}>{r.recipientName}</PushButton>
                            </div>
                        )}
                    </div>
                </>
                }
                {(selectedRecipient != undefined && recipientName) &&
                <>
                    <div className={classNames(layout.vertical, layout.center, layout.alignCenter)}>
                        <div className={classNames(layout.horizontal, layout.center, layout.alignCenter)}>
                            {card == undefined &&
                            <div className={classNames(theme.moreContrast, layout.vertical, styles.boxRight)}>
                                <div>
                                    {t("LearnNow")}
                                </div>
                            </div>
                            }
                            {card &&
                            <div className={classNames(layout.horizontal)}>
                                <div className={classNames(theme.moreContrast, layout.vertical, styles.boxLeft)}>
                                    <div>{t("CardDetected")}</div>
                                    <div>
                                        <h5 className={layout.fixed}>{atob(card.uid)}</h5>
                                    </div>
                                </div>
                                <div className={classNames(theme.lessContrast, layout.vertical, styles.boxRight, layout.paddingLarge)}>
                                    <div>{t("ConfirmLearnedCard", {recipientName})}</div>
                                    <div className={classNames(layout.center, layout.alignCenter, styles.buttons)}>
                                        <PushButton
                                            primary
                                            className={classNames(styles.button, styles.marginBottom)}
                                            onClick={handleConfirm}
                                        >
                                            {t("Confirm")}
                                        </PushButton>
                                        <PushButton
                                            className={classNames(styles.button, styles.marginBottom)}
                                            onClick={handleCancel}
                                        >
                                            {t("Cancel")}
                                        </PushButton>
                                    </div>
                                </div>
                            </div>
                            }
                        </div>
                    </div>
                </>}
            </ViewContent>
        </View>
    );
}

const styles = stylesheet({
    learnPickupKey: {
        padding: "0.75em 1.5em",
        width: "40em",
    },
    recipientListElement: {
        paddingTop: "0.74em"
    },
    heading: {
        fontSize: "1.25em",
        fontWeight: 500,
        margin: "0.66em 0"
    },
    buttons: {
        paddingTop: "0.75em",
        marginLeft: 'auto',
        marginRight: 'auto'
    },
    button: {
        alignSelf: "center",
        margin: "0.25em"
    },
    branding: {
        fontSize: "2em",
        fontWeight: 300,
        color: "var(--color-primary)"
    },
    boxRight: {
        width: "25em",
        padding: "1.5em"
    },
    boxLeft: {
        width: "15em",
        padding: "1.5em"
    },
    marginBottom: {
        marginBottom: "0.5rem"
    }
});